import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input, Label } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { IndustrySelect } from "../components/onboarding/IndustrySelect";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ChevronRight, Check, Sparkles } from "lucide-react";
import Footer from "../components/Footer";
import { RecommendationCard } from "../components/valueprint/ApplyWizard";
import { Textarea } from "../components/ui/input";

const STEPS = ["Entity", "Industry", "Value Chain", "Frameworks", "Review"];

const ENTITY_TYPES = [
  "Listed Company (SGX Mainboard)",
  "Listed Company (SGX Catalist)",
  "Large Non-Listed Company",
  "Small/Medium Enterprise (SME)",
  "Subsidiary",
];

const FRAMEWORKS_CHOICES = [
  { code: "IFRS-S2", label: "IFRS S2 — Climate Disclosures" },
  { code: "SGX-CORE", label: "SGX Core ESG Metrics" },
  { code: "GHG-PROTOCOL", label: "GHG Protocol" },
  { code: "TCFD", label: "TCFD" },
  { code: "GRI", label: "GRI Standards" },
  { code: "SASB", label: "SASB" },
  { code: "SBTI", label: "Science Based Targets" },
];

export default function Onboarding() {
  const { user, tenant, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [industries, setIndustries] = useState([]);
  const [form, setForm] = useState({
    legal_entity_type: "",
    industry_code: "",
    market_cap_sgd: "",
    revenue_sgd: "",
    total_assets_sgd: "",
    employees: "",
    fy_end_month: 12,
    cpa_taxable: false,
    eca_registered: false,
    frameworks_selected: ["IFRS-S2", "SGX-CORE", "GHG-PROTOCOL"],
  });
  const [vpDesc, setVpDesc] = useState("");
  const [vpRec, setVpRec] = useState(null);
  const [vpChoice, setVpChoice] = useState(null);
  const [vpBusy, setVpBusy] = useState(false);

  const vpRecommend = async () => {
    if (vpDesc.trim().length < 10) { toast.error("Describe your business first"); return; }
    setVpBusy(true);
    try {
      const { data } = await api.post("/valueprint/recommend", { business_description: vpDesc });
      setVpRec(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Recommendation failed"); } finally { setVpBusy(false); }
  };

  useEffect(() => {
    if (!user) navigate("/login");
    if (tenant?.onboarded) navigate("/");
    document.documentElement.classList.add("dark");
    api.get("/tenants/industries").then((r) => setIndustries(r.data.industries || [])).catch(() => {});
  }, [user, tenant]);

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    try {
      const payload = {
        ...form,
        market_cap_sgd: form.market_cap_sgd ? parseFloat(form.market_cap_sgd) : null,
        revenue_sgd: form.revenue_sgd ? parseFloat(form.revenue_sgd) : null,
        total_assets_sgd: form.total_assets_sgd ? parseFloat(form.total_assets_sgd) : null,
        employees: form.employees ? parseInt(form.employees) : null,
      };
      await api.post("/tenants/me/onboard", payload);
      if (vpChoice) {
        try {
          await api.post("/valueprint/apply", { code: vpChoice.code, role: "primary", ai_confidence: vpChoice.confidence ?? null });
        } catch (e) { toast.error("ValuePrint template could not be applied — you can apply it later from Settings → Lestari ValuePrint"); }
      }
      toast.success("Onboarding complete");
      await refresh();
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Onboarding failed");
    }
  };

  const toggleFramework = (code) => {
    setForm((f) => ({
      ...f,
      frameworks_selected: f.frameworks_selected.includes(code)
        ? f.frameworks_selected.filter((c) => c !== code)
        : [...f.frameworks_selected, code],
    }));
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground dark">
      <header className="border-b border-border bg-card px-6 py-4 flex items-center justify-between">
        <div className="font-display font-bold tracking-tight">LESTARI · Onboarding</div>
        <Button variant="ghost" size="sm" onClick={logout}>Sign out</Button>
      </header>

      <div className="max-w-3xl w-full mx-auto px-6 py-12 flex-1">
        {/* Stepper */}
        <div className="flex items-center justify-between mb-12">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center flex-1 last:flex-none">
              <div className={`flex items-center gap-3 ${i === step ? "" : "opacity-60"}`}>
                <div className={`h-8 w-8 flex items-center justify-center rounded-sm text-xs font-mono ${
                  i < step ? "bg-accent text-accent-foreground" : i === step ? "border-2 border-accent" : "border border-border"
                }`}>
                  {i < step ? <Check className="h-4 w-4" /> : i + 1}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Step {i + 1}</div>
                  <div className="text-sm font-medium">{s}</div>
                </div>
              </div>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-border mx-4" />}
            </div>
          ))}
        </div>

        <div className="card-flat bg-surface p-8 space-y-6">
          {step === 0 && (
            <div className="space-y-4" data-testid="onboarding-step-entity">
              <h2 className="font-display text-2xl font-bold">Entity profile</h2>
              <p className="text-sm text-muted-foreground">Used to auto-classify your regulatory tier and obligations.</p>
              <div>
                <Label>Legal entity type *</Label>
                <Select value={form.legal_entity_type} onValueChange={(v) => setForm({ ...form, legal_entity_type: v })}>
                  <SelectTrigger data-testid="entity-type-select"><SelectValue placeholder="Choose entity type" /></SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Revenue (SGD)</Label>
                  <Input type="number" value={form.revenue_sgd} onChange={(e) => setForm({...form, revenue_sgd: e.target.value})} placeholder="480000000" data-testid="revenue-input" />
                </div>
                <div>
                  <Label>Total assets (SGD)</Label>
                  <Input type="number" value={form.total_assets_sgd} onChange={(e) => setForm({...form, total_assets_sgd: e.target.value})} placeholder="2300000000" data-testid="assets-input" />
                </div>
                <div>
                  <Label>Market cap (SGD)</Label>
                  <Input type="number" value={form.market_cap_sgd} onChange={(e) => setForm({...form, market_cap_sgd: e.target.value})} placeholder="Optional" />
                </div>
                <div>
                  <Label>Employees</Label>
                  <Input type="number" value={form.employees} onChange={(e) => setForm({...form, employees: e.target.value})} placeholder="720" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-2">
                <label className="flex items-start gap-3 p-3 border border-border rounded-sm cursor-pointer hover:bg-surface-hover" data-testid="cpa-toggle">
                  <input type="checkbox" checked={form.cpa_taxable} onChange={(e) => setForm({...form, cpa_taxable: e.target.checked})} className="mt-0.5" />
                  <div>
                    <div className="text-sm font-medium">CPA Taxable</div>
                    <div className="text-xs text-muted-foreground">Annual emissions ≥ 25k tCO2e</div>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 border border-border rounded-sm cursor-pointer hover:bg-surface-hover" data-testid="eca-toggle">
                  <input type="checkbox" checked={form.eca_registered} onChange={(e) => setForm({...form, eca_registered: e.target.checked})} className="mt-0.5" />
                  <div>
                    <div className="text-sm font-medium">ECA Registered</div>
                    <div className="text-xs text-muted-foreground">Energy Conservation Act</div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="onboarding-step-industry">
              <h2 className="font-display text-2xl font-bold">Industry classification</h2>
              <p className="text-sm text-muted-foreground">Mapped to SASB SICS — drives material KPI recommendations.</p>
              <div>
                <Label>SASB Industry *</Label>
                <IndustrySelect value={form.industry_code} onChange={(v) => setForm({ ...form, industry_code: v })} testId="industry-select" placeholder="Choose industry" />
              </div>
              <div>
                <Label>Fiscal year end month</Label>
                <Select value={String(form.fy_end_month)} onValueChange={(v) => setForm({ ...form, fy_end_month: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) =>
                      <SelectItem key={m} value={String(m)}>Month {m}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="onboarding-step-valueprint">
              <h2 className="font-display text-2xl font-bold">Select your industry &amp; value chain template</h2>
              <p className="text-sm text-muted-foreground">
                Your Lestari ValuePrint tailors dashboards, risks, supplier categories, data attributes and KPIs to how
                your business actually works. Describe your business and let the AI recommend a template — or skip and
                apply one later from Settings → Lestari ValuePrint.
              </p>
              <div>
                <Label>What does your organisation do?</Label>
                <Textarea rows={3} value={vpDesc} onChange={(e) => setVpDesc(e.target.value)}
                  placeholder="e.g. We run refrigerated trucking and cold-store warehousing for food distributors…"
                  data-testid="onboarding-vp-description" />
              </div>
              <Button variant="outline" onClick={vpRecommend} disabled={vpBusy} data-testid="onboarding-vp-recommend-btn">
                <Sparkles className="h-3.5 w-3.5" /> {vpBusy ? "Analysing…" : "Get AI recommendation"}
              </Button>
              <RecommendationCard rec={vpRec} busy={false} acceptLabel="Use this template"
                onAccept={(r) => { setVpChoice(r); toast.success(`${r.vertical_name} will be applied when you complete onboarding`); }} />
              {vpChoice && (
                <div className="flex items-center gap-2 text-sm text-[#00E65B]" data-testid="onboarding-vp-selected">
                  <Check className="h-4 w-4" /> {vpChoice.industry_name} → {vpChoice.vertical_name} selected
                  <Button variant="ghost" size="sm" className="text-xs" onClick={() => setVpChoice(null)} data-testid="onboarding-vp-clear">Clear</Button>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="onboarding-step-frameworks">
              <h2 className="font-display text-2xl font-bold">Reporting frameworks</h2>
              <p className="text-sm text-muted-foreground">Select frameworks you&apos;ll report against. Mandatory ones in Singapore are pre-selected.</p>
              <div className="grid grid-cols-1 gap-2">
                {FRAMEWORKS_CHOICES.map((f) => (
                  <label key={f.code} className="flex items-center gap-3 p-3 border border-border rounded-sm cursor-pointer hover:bg-surface-hover" data-testid={`framework-${f.code}`}>
                    <input
                      type="checkbox"
                      checked={form.frameworks_selected.includes(f.code)}
                      onChange={() => toggleFramework(f.code)}
                    />
                    <div className="text-sm font-medium">{f.label}</div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4" data-testid="onboarding-step-review">
              <h2 className="font-display text-2xl font-bold">Review</h2>
              <div className="space-y-3 font-mono text-sm">
                <div><span className="text-muted-foreground">Entity Type:</span> {form.legal_entity_type || "—"}</div>
                <div><span className="text-muted-foreground">Industry:</span> {industries.find(i => i.code === form.industry_code)?.name || "—"}</div>
                <div><span className="text-muted-foreground">ValuePrint:</span> {vpChoice ? `${vpChoice.industry_name} → ${vpChoice.vertical_name} v${vpChoice.version}` : "Deferred — apply later from Settings"}</div>
                <div><span className="text-muted-foreground">Revenue:</span> SGD {form.revenue_sgd ? Number(form.revenue_sgd).toLocaleString() : "—"}</div>
                <div><span className="text-muted-foreground">Frameworks:</span> {form.frameworks_selected.join(", ")}</div>
                <div className="flex gap-2 flex-wrap pt-2">
                  {form.cpa_taxable && <Badge variant="warning">CPA</Badge>}
                  {form.eca_registered && <Badge variant="warning">ECA</Badge>}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between pt-6 border-t border-border">
            <Button variant="outline" onClick={prev} disabled={step === 0} data-testid="onboarding-prev-btn">Back</Button>
            {step < STEPS.length - 1 ? (
              <Button variant="primary" onClick={next} data-testid="onboarding-next-btn" disabled={
                (step === 0 && !form.legal_entity_type) || (step === 1 && !form.industry_code)
              }>
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button variant="primary" onClick={submit} data-testid="onboarding-submit-btn">Complete onboarding</Button>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
